import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Spin, message,
  Space, Layout,
} from 'antd';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import RunDateFilter from '../../components/RunDateFilter';

const { Title, Text } = Typography;
const { Content } = Layout;


export const emptyTotals = {
  totalEarnings: 0, coveredEarnings: 0, ownEarnings: 0, grossJobValue: 0,
  dueToLite: 0, dueToOpc: 0, dueToExternal: 0, cxEarnings: 0, cxJobs: 0,
  jobsDone: 0, jobsCovered: 0, ownJobs: 0, daysWorked: 0,
  activeDrivers: 0, activeVans: 0, avgPerJob: 0,
};

const fmt = (v) => `£${Number(v || 0).toFixed(2)}`;

const DailyRunOverview = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [data, setData] = useState({ totals: emptyTotals, earningsByDay: [], fuel: { total: 0, byDriver: [] } });

  const handleFilterChange = (range) => {
    if (range) setDateRange(range);
  };

  const fetchData = useCallback(async (range) => {
    if (!range || !range[0] || !range[1]) return;
    try {
      setLoading(true);
      const res = await apiService.dailyRuns.getOverview({
        startDate: range[0].format('YYYY-MM-DD'),
        endDate: range[1].format('YYYY-MM-DD'),
      });
      setData(res.data);
    } catch {
      message.error('Failed to load overview data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(dateRange);
  }, [fetchData, dateRange]);

  const { totals, earningsByDay, fuel } = data;

  return (
    <Layout>
      <Content style={{ padding: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>Overview</Title>
            <Text type="secondary">Earnings and job activity for the selected period.</Text>
          </div>

          <RunDateFilter onChange={handleFilterChange} />

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* Row 1 — Earnings */}
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title="Total Earnings (End of Day)" value={totals.totalEarnings} precision={2} prefix="£" valueStyle={{ color: '#3f8600' }} />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>VAT (20%): £{(totals.totalEarnings * 0.2).toFixed(2)}</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title="Own Driver Earnings" value={totals.ownEarnings} precision={2} prefix="£" valueStyle={{ color: '#3f8600' }} />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>VAT (20%): £{(totals.ownEarnings * 0.2).toFixed(2)}</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title="Lite Jobs (15%)" value={totals.coveredEarnings} precision={2} prefix="£" valueStyle={{ color: '#fa8c16' }} />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>VAT (20%): £{(totals.coveredEarnings * 0.2).toFixed(2)}</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title="Gross Job Value" value={totals.grossJobValue} precision={2} prefix="£" />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>VAT (20%): £{(totals.grossJobValue * 0.2).toFixed(2)}</div>
                  </Card>
                </Col>
              </Row>

              {/* Row 2 — Payments due */}
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title="Due to Lite" value={totals.dueToLite} precision={2} prefix="£" valueStyle={{ color: '#cf1322' }} />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>VAT (20%): £{(totals.dueToLite * 0.2).toFixed(2)}</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title="Due to OPC (CX)" value={totals.dueToOpc} precision={2} prefix="£" valueStyle={{ color: '#1677ff' }} />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>VAT (20%): £{(totals.dueToOpc * 0.2).toFixed(2)}</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title="Due to External" value={totals.dueToExternal} precision={2} prefix="£" valueStyle={{ color: '#fa541c' }} />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>VAT (20%): £{(totals.dueToExternal * 0.2).toFixed(2)}</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title="Jobs Done" value={totals.jobsDone} />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{totals.ownJobs} own · {totals.jobsCovered} covered</div>
                  </Card>
                </Col>
              </Row>

              {/* Row 3 — Activity + Fuel */}
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={5}>
                  <Card>
                    <Statistic title="Own Jobs" value={totals.ownJobs} />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={5}>
                  <Card>
                    <Statistic title="Lite Jobs (15%)" value={totals.jobsCovered - totals.cxJobs} />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Jobs Lite provided driver</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={5}>
                  <Card>
                    <Statistic title="Days Worked" value={totals.daysWorked} />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={5}>
                  <Card>
                    <Statistic title="Avg Earning / Job" value={totals.avgPerJob} precision={2} prefix="£" />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>VAT (20%): £{(totals.avgPerJob * 0.2).toFixed(2)}</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                  <Card>
                    <Statistic title="Total Fuel Spend" value={fuel.total} precision={2} prefix="£" valueStyle={{ color: '#ff7a45' }} />
                    {totals.totalEarnings > 0 && (
                      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        {((fuel.total / totals.totalEarnings) * 100).toFixed(1)}% of earnings
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Chart */}
              <Card title="Earnings by Day">
                <div style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={earningsByDay} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(d) => dayjs(d).format('DD MMM')} />
                      <YAxis tickFormatter={(v) => `£${v}`} />
                      <Tooltip
                        formatter={(v, name) => [fmt(v), name]}
                        labelFormatter={(d) => dayjs(d).format('ddd DD MMM YYYY')}
                        contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }}
                        itemStyle={{ color: '#fff' }}
                        labelStyle={{ color: '#fff', fontWeight: 600 }}
                        cursor={false}
                      />
                      <Legend />
                      <Bar dataKey="earnings" name="Earnings" fill="#00C49F" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </>
          )}
        </Space>
      </Content>
    </Layout>
  );
};

export default DailyRunOverview;
